import React, { useCallback, useState } from "react";

import {
  Chip,
  Button,
  Switch,
  Tooltip,
  Checkbox,
  TableRow,
  TextField,
  TableCell,
  IconButton,
  FormControl,
  Autocomplete,
  createFilterOptions,
} from "@mui/material";
import {
  CopyAll as CopyAllIcon,
  CheckCircle as CheckCircleIcon,
  InfoOutlined as InfoOutlinedIcon,
  RemoveCircle as RemoveCircleIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";

import { isValidArray } from "utils/array";
import { addPrefixToString } from "utils/string";

import {
  FREE_TEXT,
  ALERT_NOTIFICATION_METHODS,
  ALERT_NOTIFICATION_MESSAGE_CONTENT,
} from "static/constants";

const NotificationPreferenceItem = ({
  configIndex,
  notifyConfig,
  notifyConfigIndex,
  timePeriodIndex,
  previousNotifyConfig,
  canRemoveConfig,
  onRemoveConfig,
  isEditPreference,
  formikProps: { errors, touched, setFieldValue, setFieldTouched },
}) => {
  const {
    active,
    recipients,
    message_content,
    notification_method,
    include_transmission_pdf,
  } = notifyConfig;

  const filter = createFilterOptions();

  const [inputValue, setInputValue] = useState("");

  const handleKeyDown = (event) => {
    const recipientsPath = `configurations[${configIndex}].notify_configs[${notifyConfigIndex}].recipients`;

    if (event.key === "Backspace" && !inputValue && recipients?.length) {
      const updatedRecipients = [...recipients];
      const lastRecipient = updatedRecipients.pop();
      setFieldValue(recipientsPath, updatedRecipients);
      setInputValue(lastRecipient || "");
    }

    if (event.key === "Enter" && inputValue) {
      const newRecipient = inputValue.trim();
      if (newRecipient) {
        setFieldValue(recipientsPath, [...recipients, newRecipient]);
        setInputValue("");
      }
    }
  };

  const getFieldError = useCallback(
    (fieldName) =>
      touched?.configurations?.[configIndex]?.notify_configs?.[
        notifyConfigIndex
      ]?.[fieldName] &&
      errors?.configurations?.[configIndex]?.notify_configs?.[
        notifyConfigIndex
      ]?.[fieldName],
    [
      configIndex,
      errors?.configurations,
      notifyConfigIndex,
      touched?.configurations,
    ]
  );

  const freeText = notifyConfig?.message_content?.find(
    (item) => item?.title === FREE_TEXT
  );

  const previousMessageContent = previousNotifyConfig?.message_content;
  const canCopyPreviousMessageContent =
    isValidArray(previousMessageContent) &&
    previousMessageContent?.length > 0 &&
    previousNotifyConfig?.notification_method !== notification_method;

  const handleCopyPreviousMessageContent = () => {
    if (!canCopyPreviousMessageContent) {
      return;
    }

    const copiedMessageContent = previousMessageContent.map(
      ({ title, free_text }) => ({
        title,
        free_text,
      })
    );

    setFieldValue(
      `configurations[${configIndex}].notify_configs[${notifyConfigIndex}].message_content`,
      copiedMessageContent
    );
  };

  const renderRecipientsCell = () => {
    if (
      isEditPreference &&
      notification_method === ALERT_NOTIFICATION_METHODS.email
    ) {
      return (
        <FormControl fullWidth>
          <Autocomplete
            multiple
            freeSolo
            id="recipients"
            value={recipients || []}
            options={[]}
            inputValue={inputValue}
            onInputChange={(e, value) => setInputValue(value)}
            onKeyDown={handleKeyDown}
            onBlur={() =>
              setFieldTouched(
                `configurations[${configIndex}].notify_configs[${notifyConfigIndex}].recipients`
              )
            }
            onChange={(e, newValue) => {
              const cleanedRecipients = newValue
                .map((item) => item.trim())
                .filter(Boolean);
              setFieldValue(
                `configurations[${configIndex}].notify_configs[${notifyConfigIndex}].recipients`,
                cleanedRecipients
              );
            }}
            onClose={() => {
              const newRecipient = inputValue.trim();
              if (newRecipient) {
                setFieldValue(
                  `configurations[${configIndex}].notify_configs[${notifyConfigIndex}].recipients`,
                  [...recipients, newRecipient]
                );

                setInputValue("");
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Recipients"
                required
                variant="outlined"
                color="secondary"
                error={Boolean(getFieldError("recipients"))}
                helperText={getFieldError("recipients")}
              />
            )}
          />
        </FormControl>
      );
    }

    if (
      notification_method === ALERT_NOTIFICATION_METHODS.slack &&
      !isValidArray(recipients)
    ) {
      return (
        <div className="d-flex align-items-center">
          <span className="mr-1"> No Slack recipients.</span>
          <Tooltip title="Contact the technical team to add Slack recipients.">
            <InfoOutlinedIcon
              color="info"
              fontSize="small"
              className="cursor-pointer"
            />
          </Tooltip>
        </div>
      );
    }

    return (
      <div className="d-flex flex-wrap">
        {isValidArray(recipients)
          ? recipients.map((recipient) => (
              <Chip
                key={recipient}
                label={
                  notification_method === ALERT_NOTIFICATION_METHODS.slack
                    ? addPrefixToString(recipient)
                    : recipient
                }
                className="mr-2 mb-2"
                sx={{
                  maxWidth: "100%",
                  height: "auto",
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  },
                }}
              />
            ))
          : "Not added"}
      </div>
    );
  };

  return (
    <>
      {!notifyConfig.same_as_regular && (
        <TableRow style={{ verticalAlign: "top" }}>
          <TableCell>{notification_method || "Not added"}</TableCell>
          <TableCell>{renderRecipientsCell()}</TableCell>
          <TableCell>
            <div className="d-flex flex-column">
              {isEditPreference ? (
                <>
                  <FormControl fullWidth>
                    <Autocomplete
                      multiple
                      disableClearable
                      freeSolo={false}
                      disableCloseOnSelect
                      id="message_content"
                      value={message_content || []}
                      options={ALERT_NOTIFICATION_MESSAGE_CONTENT.map(
                        (content) => ({ title: content, free_text: "" })
                      )}
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);
                        return filtered.length ? filtered : options;
                      }}
                      getOptionLabel={(option) => option.title}
                      isOptionEqualToValue={(option, value) =>
                        option.title === value.title
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            checked={selected}
                            style={{ marginRight: 8 }}
                          />
                          {option.title}
                        </li>
                      )}
                      onBlur={(e) =>
                        setFieldTouched(
                          `configurations[${configIndex}].notify_configs[${notifyConfigIndex}].message_content`
                        )
                      }
                      onChange={(e, value) => {
                        const updatedContents = value?.map(
                          ({ title, free_text }) => ({
                            title,
                            free_text,
                          })
                        );
                        setFieldValue(
                          `configurations[${configIndex}].notify_configs[${notifyConfigIndex}].message_content`,
                          updatedContents
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Message Content"
                          required
                          variant="outlined"
                          color="secondary"
                          inputProps={{ ...params.inputProps, readOnly: true }}
                          error={Boolean(getFieldError("message_content"))}
                          helperText={getFieldError("message_content")}
                        />
                      )}
                    />
                  </FormControl>

                  {canCopyPreviousMessageContent && (
                    <div className="d-flex justify-end mb-2">
                      <Button
                        variant="text"
                        color="info"
                        size="small"
                        onClick={handleCopyPreviousMessageContent}
                        startIcon={<CopyAllIcon fontSize="small" />}
                      >
                        Copy from {previousNotifyConfig?.notification_method}
                      </Button>
                    </div>
                  )}

                  {freeText && (
                    <FormControl
                      fullWidth
                      error={
                        touched?.free_text &&
                        (!freeText?.free_text?.trim() ||
                          freeText?.free_text?.length > 100)
                      }
                    >
                      <TextField
                        label="Free Text"
                        required
                        fullWidth
                        id="free_text"
                        margin="normal"
                        name="free_text"
                        variant="outlined"
                        maxRows={3}
                        value={freeText?.free_text || ""}
                        error={
                          touched?.free_text &&
                          (!freeText?.free_text?.trim() ||
                            freeText?.free_text?.length > 100)
                        }
                        helperText={
                          touched?.free_text &&
                          ((!freeText?.free_text?.trim() &&
                            "Free text is required.") ||
                            (freeText?.free_text?.length > 100 &&
                              "Free Text can have maximum 100 characters."))
                        }
                        onBlur={() => setFieldTouched("free_text")}
                        onChange={(e) => {
                          setFieldValue(
                            `configurations[${configIndex}].notify_configs[${notifyConfigIndex}].message_content`,
                            notifyConfig?.message_content?.map((item) =>
                              item?.title === FREE_TEXT
                                ? { ...item, free_text: e.target.value }
                                : item
                            )
                          );
                        }}
                      />
                    </FormControl>
                  )}
                </>
              ) : (
                <div className="d-flex flex-wrap">
                  {isValidArray(message_content)
                    ? message_content.map((content) => {
                        const label =
                          content.title === FREE_TEXT
                            ? content.free_text
                            : content.title;

                        return (
                          <Chip
                            key={label}
                            label={label}
                            className="mr-2 mb-2"
                            sx={{
                              maxWidth: "100%",
                              height: "auto",
                              "& .MuiChip-label": {
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                              },
                            }}
                          />
                        );
                      })
                    : "Not added"}
                </div>
              )}
            </div>
          </TableCell>
          <TableCell>
            {notification_method === ALERT_NOTIFICATION_METHODS.email ? (
              isEditPreference ? (
                <Switch
                  name="include_transmission_pdf"
                  color="secondary"
                  checked={notifyConfig.include_transmission_pdf || false}
                  onBlur={(e) =>
                    setFieldTouched(
                      `configurations[${configIndex}].notify_configs[${notifyConfigIndex}].include_transmission_pdf`
                    )
                  }
                  onChange={(e, checked) => {
                    setFieldValue(
                      `configurations[${configIndex}].notify_configs[${notifyConfigIndex}].include_transmission_pdf`,
                      checked
                    );
                  }}
                />
              ) : include_transmission_pdf ? (
                <Tooltip title={"Enabled"}>
                  <CheckCircleIcon style={{ color: "#94BA33" }} />
                </Tooltip>
              ) : (
                <Tooltip title={"Disabled"}>
                  <RemoveCircleIcon style={{ color: "red" }} />
                </Tooltip>
              )
            ) : (
              "N/A"
            )}
          </TableCell>
          <TableCell>
            {isEditPreference ? (
              <Switch
                name="active"
                color="secondary"
                checked={notifyConfig.active || false}
                onBlur={(e) =>
                  setFieldTouched(
                    `configurations[${configIndex}].notify_configs[${notifyConfigIndex}].active`
                  )
                }
                onChange={(e, checked) => {
                  setFieldValue(
                    `configurations[${configIndex}].notify_configs[${notifyConfigIndex}].active`,
                    checked
                  );
                }}
              />
            ) : active ? (
              <Tooltip title={"Enabled"}>
                <CheckCircleIcon style={{ color: "#94BA33" }} />
              </Tooltip>
            ) : (
              <Tooltip title={"Disabled"}>
                <RemoveCircleIcon style={{ color: "red" }} />
              </Tooltip>
            )}
          </TableCell>
          <TableCell>
            {isEditPreference && canRemoveConfig && timePeriodIndex > 0 && (
              <Tooltip title="Remove configuration">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() =>
                      onRemoveConfig(configIndex, notifyConfigIndex)
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default NotificationPreferenceItem;
